import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.franchisee.editors.FranchiseeBranchEditor.';

  public static readonly branchSettings = `${Id.prefix}branchSettings`;
  public static readonly branchName = `${Id.prefix}branchName`;
  public static readonly abbreviatedName = `${Id.prefix}abbreviatedName`;
  public static readonly taxRegistrationNumber = `${Id.prefix}taxRegistrationNumber`;
  public static readonly branchAddress = `${Id.prefix}branchAddress`;
  public static readonly physicalAddress = `${Id.prefix}physicalAddress`;
  public static readonly billingAddress = `${Id.prefix}billingAddress`;
  public static readonly hidden = `${Id.prefix}hidden`;
  public static readonly branchStatus = `${Id.prefix}branchStatus`;
  public static readonly statusActive = `${Id.prefix}statusActive`;
  public static readonly statusDeactivated = `${Id.prefix}statusDeactivated`;

  // Dialogs and validation
  public static readonly addBranch = `${Id.prefix}addBranch`;
  public static readonly editBranch = `${Id.prefix}editBranch`;
  public static readonly provideTaxRegistrationNumber = `${Id.prefix}provideTaxRegistrationNumber`;
  public static readonly provideBranchName = `${Id.prefix}provideBranchName`;
  public static readonly provideBranchAbbreviatedName = `${Id.prefix}provideBranchAbbreviatedName`;
  public static readonly provideStreetAddress = `${Id.prefix}provideStreetAddress`;
  public static readonly provideZipCode = `${Id.prefix}provideZipCode`;
  public static readonly provideBillingStreetAddress = `${Id.prefix}provideBillingStreetAddress`;
  public static readonly provideBillingAddressPostcode = `${Id.prefix}provideBillingAddressPostcode`;
  public static readonly cancelNewEdit = `${Id.prefix}cancelNewEdit`;
  public static readonly close = `${Id.prefix}close`;
  public static readonly saveBranch = `${Id.prefix}saveBranch`;
  public static readonly labelName = `${Id.prefix}name`;
  public static readonly lastBranchCannotBeHidden = `${Id.prefix}lastBranchCannotBeHidden`;
  public static readonly cannotChangeBranchHidden = `${Id.prefix}cannotChangeBranchHidden`;
  public static readonly cannotHideBranchWithSingleBranchUsers = `${Id.prefix}cannotHideBranchWithSingleBranchUsers`;
  public static readonly confirmDeactivateBranch = `${Id.prefix}confirmDeactivateBranch`;
  public static readonly confirmActivateBranch = `${Id.prefix}confirmActivateBranch`;
  public static readonly confirmDeactivateBranchTitle = `${Id.prefix}confirmDeactivateBranchTitle`;
  public static readonly confirmActivateBranchTitle = `${Id.prefix}confirmActivateBranchTitle`;
}

export class LangFranchiseeBranchEditor {
  private constructor() {}

  public static branchSettings(): string {
    return interpolate(Id.branchSettings);
  }

  public static branchName(): string {
    return interpolate(Id.branchName);
  }

  public static abbreviatedName(): string {
    return interpolate(Id.abbreviatedName);
  }

  public static taxRegistrationNumber(): string {
    return interpolate(Id.taxRegistrationNumber);
  }

  public static branchAddress(): string {
    return interpolate(Id.branchAddress);
  }

  public static physicalAddress(): string {
    return interpolate(Id.physicalAddress);
  }

  public static billingAddress(): string {
    return interpolate(Id.billingAddress);
  }

  public static addBranch(): string {
    return interpolate(Id.addBranch);
  }

  public static editBranch(): string {
    return interpolate(Id.editBranch);
  }

  public static provideTaxRegistrationNumber(): string {
    return interpolate(Id.provideTaxRegistrationNumber);
  }

  public static provideBranchName(): string {
    return interpolate(Id.provideBranchName);
  }

  public static provideBranchAbbreviatedName(): string {
    return interpolate(Id.provideBranchAbbreviatedName);
  }

  public static provideStreetAddress(): string {
    return interpolate(Id.provideStreetAddress);
  }

  public static provideZipCode(): string {
    return interpolate(Id.provideZipCode);
  }

  public static provideBillingStreetAddress(): string {
    return interpolate(Id.provideBillingStreetAddress);
  }

  public static provideBillingAddressPostcode(): string {
    return interpolate(Id.provideBillingAddressPostcode);
  }

  public static cancelNewEdit(): string {
    return interpolate(Id.cancelNewEdit);
  }

  public static close(): string {
    return interpolate(Id.close);
  }

  public static saveBranch(): string {
    return interpolate(Id.saveBranch);
  }

  public static labelName(): string {
    return interpolate(Id.labelName);
  }

  public static hidden(): string {
    return interpolate(Id.hidden);
  }

  public static branchStatus(): string {
    return interpolate(Id.branchStatus);
  }

  public static statusActive(): string {
    return interpolate(Id.statusActive);
  }

  public static statusDeactivated(): string {
    return interpolate(Id.statusDeactivated);
  }

  public static lastBranchCannotBeHidden(): string {
    return interpolate(Id.lastBranchCannotBeHidden);
  }

  public static cannotChangeBranchHidden(): string {
    return interpolate(Id.cannotChangeBranchHidden);
  }

  public static cannotHideBranchWithSingleBranchUsers(count: number): string {
    return interpolate(Id.cannotHideBranchWithSingleBranchUsers, { count });
  }

  public static confirmDeactivateBranch(): string {
    return interpolate(Id.confirmDeactivateBranch);
  }

  public static confirmActivateBranch(): string {
    return interpolate(Id.confirmActivateBranch);
  }

  public static confirmDeactivateBranchTitle(): string {
    return interpolate(Id.confirmDeactivateBranchTitle);
  }

  public static confirmActivateBranchTitle(): string {
    return interpolate(Id.confirmActivateBranchTitle);
  }
}

export class FranchiseeBranchEditorModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.branchSettings, "{{glossary 'branch'}} Settings", 'Heading for branch settings section');
    register.add(Id.branchName, "{{glossary 'branch'}} Name", 'Label for branch name column');
    register.add(Id.abbreviatedName, 'Abbreviated Name', 'Label for branch abbreviated name column');
    register.add(Id.taxRegistrationNumber, 'Tax Registration #', 'Label for branch tax registration number');
    register.add(Id.branchAddress, 'Branch Address', 'Section title for branch address');
    register.add(Id.physicalAddress, 'Physical Address', 'Label for physical address column');
    register.add(Id.billingAddress, 'Billing Address', 'Label for billing address column');

    // Dialogs and validation
    register.add(Id.addBranch, "Add {{glossary 'branch'}}", 'Dialog title for adding a branch');
    register.add(Id.editBranch, "Edit {{glossary 'branch'}}", 'Dialog title for editing a branch');
    register.add(
      Id.provideTaxRegistrationNumber,
      "Please provide a {{glossary 'tax'}} Registration Number",
      'Validation for tax registration number'
    );
    register.add(Id.provideBranchName, "Please provide a {{glossary 'branch'}} name", 'Validation for branch name');
    register.add(
      Id.provideBranchAbbreviatedName,
      "Please provide a {{glossary 'branch'}} abbreviated name",
      'Validation for branch abbreviated name'
    );
    register.add(Id.provideStreetAddress, 'Please provide a Street Address', 'Validation for street address');
    register.add(Id.provideZipCode, 'Please provide a Zip Code', 'Validation for zip code');
    register.add(
      Id.provideBillingStreetAddress,
      'Please provide a Billing Street Address',
      'Validation for billing street address'
    );
    register.add(
      Id.provideBillingAddressPostcode,
      'Please provide a Billing Address Postcode',
      'Validation for billing address postcode'
    );
    register.add(Id.cancelNewEdit, 'Cancel New Edit', 'Cancel new edit dialog');
    register.add(Id.close, 'Close', 'Button label for close');
    register.add(Id.saveBranch, "Save {{glossary 'branch'}}", 'Button label for save branch');
    register.add(Id.labelName, 'Name', 'Label for branch name field');
    register.add(
      Id.hidden,
      "Deactivate {{glossary 'branch'}}",
      'Label for branch deactivate toggle - when on, the branch is deactivated and hidden from ordinary dealer users (Cyncly Staff and supplier agents can still view it)'
    );
    register.add(
      Id.branchStatus,
      "{{glossary 'branch'}} Status",
      'Column header for branch active/deactivated status in the branches table'
    );
    register.add(Id.statusActive, 'Active', 'Badge label when a branch is active (not hidden)');
    register.add(
      Id.statusDeactivated,
      'Deactivated',
      'Badge label when a branch is deactivated (hidden from ordinary dealer users; still visible to Cyncly Staff and supplier agents)'
    );
    register.add(
      Id.lastBranchCannotBeHidden,
      'At least one branch must remain active',
      'Validation error shown when trying to deactivate the last active branch'
    );
    register.add(
      Id.cannotChangeBranchHidden,
      "You do not have permission to activate or deactivate this {{glossary 'branch'}}",
      'Validation error when the caller is not allowed to change branch active/hidden state (allowed: Cyncly staff, Staff Admin, supplier support)'
    );
    register.add(
      Id.cannotHideBranchWithSingleBranchUsers,
      "Cannot deactivate this {{glossary 'branch'}} while {{count}} single-{{glossary 'branch'}} {{glossary 'user'}} assignment(s) remain. Reassign them to another {{glossary 'branch'}} first.",
      'Validation error when deactivating a branch that still has single-branch users assigned. {{count}} is the number of affected users.'
    );
    register.add(
      Id.confirmDeactivateBranch,
      "Are you sure you want to deactivate this {{glossary 'branch'}}? Deactivated {{glossary 'branch:plural'}} are hidden from users.",
      'Confirmation message when saving after the user turns on the Deactivate branch toggle. Deactivated branches are hidden from ordinary dealer users; Cyncly Staff and supplier agents can still view them.'
    );
    register.add(
      Id.confirmActivateBranch,
      "Are you sure you want to activate this {{glossary 'branch'}}?",
      'Confirmation message when saving after the user turns off the Deactivate branch toggle'
    );
    register.add(
      Id.confirmDeactivateBranchTitle,
      "Deactivate {{glossary 'branch'}}",
      'Modal title when confirming branch deactivation'
    );
    register.add(
      Id.confirmActivateBranchTitle,
      "Activate {{glossary 'branch'}}",
      'Modal title when confirming branch activation'
    );
  }
}
